import { useEffect, useMemo, useState } from 'react';
import { Button, Drawer, Empty, Form, Input, message, Popconfirm, Select, Space, Switch, Table, Tabs, Tag, Tooltip, Typography } from 'antd';
import { DeleteOutlined, EditOutlined, MailOutlined, PlusOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import AppCard from '../../../components/cards/AppCard';
import TipTapEditor from '../../../components/TipTapEditor';
import { getAdminClients, getModuleFields, getAvailableModules, getRowActionConfigs } from '../../../services/adminApi';
import { normalizeModuleOptions } from '../../../utils/moduleOptions';
import { createEmailTemplate, createSignature, deleteEmailTemplate, deleteSignature, getEmailTemplate, getEmailTemplates, getSignature, getSignaturePlaceholders, getSignatures, updateEmailTemplate, updateSignature, updateSignatureStatus } from '../../../services/emailTemplateApi';
import '../../../styles/FormGroupsSection.css';

const { Text } = Typography;
const EMPTY = { name: '', module: '', action: '', clientId: '', description: '', toMail: [], ccMail: [], bccMail: [], subject: '', body: '', isActive: true };
const idOf = (record) => record?._id ?? record?.id;

const ONBOARDING_MAIL_PLACEHOLDER_GROUPS = [
  {
    moduleName: 'Onboarding Employer Details',
    moduleLabel: 'Employer Details',
    fields: [
      { key: 'employerName', value: 'mailContext.employerName', label: 'Employer Name' },
      { key: 'employerCompanyName', value: 'mailContext.employerCompanyName', label: 'Employer Company Name' },
      { key: 'employerEmail', value: 'mailContext.employerEmail', label: 'Employer Email' },
      { key: 'employerContactNumber', value: 'mailContext.employerContactNumber', label: 'Employer Contact Number' },
      { key: 'employerSigningAuthorityName', value: 'mailContext.employerSigningAuthorityName', label: 'Employer Signing Authority Name' },
      { key: 'employerSigningAuthorityEmail', value: 'mailContext.employerSigningAuthorityEmail', label: 'Employer Signing Authority Email' },
      { key: 'employerSigningDocumentLink', value: 'mailContext.employerSigningDocumentLink', label: 'Employer Signing Documents Link' },
    ],
  },
  {
    moduleName: 'Onboarding Referral Details',
    moduleLabel: 'Referral Details',
    fields: [
      { key: 'referralName', value: 'mailContext.referralName', label: 'Referral Name' },
      { key: 'referralCompanyName', value: 'mailContext.referralCompanyName', label: 'Referral Company Name' },
      { key: 'referralEmail', value: 'mailContext.referralEmail', label: 'Referral Email' },
      { key: 'referralContactNumber', value: 'mailContext.referralContactNumber', label: 'Referral Contact Number' },
      { key: 'referralRecruiterName', value: 'mailContext.referralRecruiterName', label: 'Referral Recruiter Name' },
      { key: 'referralRecruiterEmail', value: 'mailContext.referralRecruiterEmail', label: 'Referral Recruiter Email' },
      { key: 'referralSigningDocumentLink', value: 'mailContext.referralSigningDocumentLink', label: 'Referral Signing Documents Link' },
    ],
  },
  {
    moduleName: 'Onboarding Signing Documents',
    moduleLabel: 'Signing Documents',
    fields: [
      { key: 'signingDocumentLink', value: 'mailContext.signingDocumentLink', label: 'Signing Documents Link' },
    ],
  },
];

function isOnboardingModule(moduleName) {
  return String(moduleName ?? '').trim().toLowerCase() === 'onboarding';
}

function normalizePlaceholderPath(value) {
  return String(value ?? '')
    .trim()
    .replace(/^{{\s*/, '')
    .replace(/\s*}}$/, '')
    .trim()
    .replace(/^\./, '')
    .trim();
}

function extractPlaceholderKeys(...values) {
  const keys = new Set();
  values.forEach((value) => {
    // Matches an optional leading dot so templates saved with the older
    // "{{.field}}" syntax still have their placeholders recorded.
    const regex = /{{\.?([\w.]+)}}/g;
    let match;
    while ((match = regex.exec(String(value ?? ''))) !== null) keys.add(match[1]);
  });
  return keys;
}

function TemplateEditor({ open, templateId, modules, clients, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [actionOptions, setActionOptions] = useState([]);
  const [allFieldOptions, setAllFieldOptions] = useState([]);
  const [preferredSources, setPreferredSources] = useState({});

  async function loadModuleActions(module) {
    if (!module) { setActionOptions([]); return; }
    const configs = await getRowActionConfigs(module).catch(() => []);
    const actions = new Map();
    configs.forEach((config) => (config.rowActions ?? []).forEach((action) => {
      if (action.key) actions.set(action.key, action.label || action.key);
    }));
    setActionOptions([...actions].map(([value, label]) => ({ value, label })));
  }

  async function loadAllModulePlaceholders() {
    const groups = await Promise.all((modules ?? []).map(async (moduleOption) => {
      const moduleName = moduleOption.value;
      const fields = await getModuleFields(moduleName).catch(() => []);
      return fields.map((field) => {
        const rawKey = field.value ?? field.field ?? field.fieldKey ?? field.key ?? field.name;
        const rawValue = field.path ?? field.value ?? field.field ?? field.fieldKey ?? field.key ?? field.name;
        return {
          key: normalizePlaceholderPath(rawKey),
          value: normalizePlaceholderPath(rawValue),
          label: field.label ?? field.fieldName ?? field.name ?? field.value,
          moduleName,
          moduleLabel: moduleOption.label ?? moduleName,
        };
      }).filter((field) => field.key && field.value);
    }));
    const flattened = groups.flat();

    // Employer/referral/signing placeholders are runtime mail-context values,
    // not normal module-data fields, so append them explicitly for onboarding.
    const hasOnboarding = (modules ?? []).some((moduleOption) => isOnboardingModule(moduleOption.value));
    if (hasOnboarding) {
      ONBOARDING_MAIL_PLACEHOLDER_GROUPS.forEach((group) => {
        group.fields.forEach((field) => {
          if (!flattened.some((item) => item.key === field.key && item.value === field.value)) {
            flattened.push({
              ...field,
              moduleName: group.moduleName,
              moduleLabel: group.moduleLabel,
            });
          }
        });
      });
    }

    setAllFieldOptions(flattened);
  }

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    queueMicrotask(() => {
      setLoading(true);
      loadAllModulePlaceholders().catch(() => {});
      const request = templateId ? getEmailTemplate(templateId) : Promise.resolve(EMPTY);
      request.then((item) => {
        if (cancelled) return;
        const values = {
          ...EMPTY,
          ...item,
          action: item?.action ? [item.action] : [],
          clientId: item?.clientIdStr || (typeof item?.clientId === 'string' ? item.clientId : ''),
        };
        form.setFieldsValue(values);
        setBody(values.body || '');

        const savedSources = {};
        (item?.placeholders ?? item?.placeHolder ?? []).forEach((group) => {
          (group?.holders ?? []).forEach((holder) => {
            if (holder?.key) { const key = normalizePlaceholderPath(holder.key); savedSources[key] = { moduleName: group.moduleName, value: normalizePlaceholderPath(holder.value) }; }
          });
        });
        setPreferredSources(savedSources);
        loadModuleActions(values.module);
      }).catch((error) => message.error(error?.message || 'Could not load template')).finally(() => { if (!cancelled) setLoading(false); });
    });
    return () => { cancelled = true; };
  }, [form, open, templateId, modules]);

  function registerPlaceholderSource(field) {
    setPreferredSources((current) => ({
      ...current,
      [field.key]: { moduleName: field.moduleName, value: field.value },
    }));
  }

  function insertPlaceholder(field) {
    const token = `{{.${field.key}}}`;
    registerPlaceholderSource(field);
    setBody((current) => `${current}${current ? ' ' : ''}${token}`);
    message.success(`Inserted ${token} from ${field.moduleLabel || field.moduleName}`);
  }

  function dragPlaceholder(event, field) {
    const token = `{{.${field.key}}}`;
    registerPlaceholderSource(field);
    event.dataTransfer.setData('text/plain', token);
    event.dataTransfer.effectAllowed = 'copy';
  }

  function resolvePlaceholderSource(key, selectedModule) {
    const preferred = preferredSources[key];
    if (preferred?.moduleName && preferred?.value) return preferred;

    const matches = allFieldOptions.filter((field) => field.key === key);
    const selectedModuleMatch = matches.find((field) => (
      field.moduleName === selectedModule
      || (isOnboardingModule(selectedModule) && String(field.moduleName).startsWith('Onboarding '))
    ));
    const field = selectedModuleMatch ?? matches[0];
    return field ? { moduleName: field.moduleName, value: field.value } : null;
  }

  async function save() {
    try {
      const values = await form.validateFields();
      if (!body.trim()) { message.error('Message body is required'); return; }
      setSaving(true);

      const used = extractPlaceholderKeys(values.subject, body);
      const grouped = new Map();
      used.forEach((key) => {
        const source = resolvePlaceholderSource(key, values.module);
        if (!source) return;
        if (!grouped.has(source.moduleName)) grouped.set(source.moduleName, []);
        grouped.get(source.moduleName).push({ key, value: source.value });
      });

      const placeholders = [...grouped].map(([moduleName, holders]) => ({ moduleName, holders }));
      const payload = {
        ...values,
        action: Array.isArray(values.action) ? values.action.at(-1) : values.action,
        body,
        placeholders,
      };
      if (!payload.clientId) delete payload.clientId;
      if (templateId) await updateEmailTemplate(templateId, payload);
      else await createEmailTemplate(payload);
      message.success(templateId ? 'Template updated' : 'Template created');
      onSaved();
    } catch (error) {
      if (!error?.errorFields) message.error(error?.message || 'Could not save template');
    } finally { setSaving(false); }
  }

  const placeholderGroups = useMemo(() => {
    const groups = new Map();
    allFieldOptions.forEach((field) => {
      if (!groups.has(field.moduleName)) groups.set(field.moduleName, { label: field.moduleLabel, fields: [] });
      groups.get(field.moduleName).fields.push(field);
    });
    return [...groups].map(([moduleName, group]) => ({ moduleName, ...group }));
  }, [allFieldOptions]);

  return (
    <Drawer open={open} onClose={onClose} width="min(1320px, 98vw)" title={templateId ? 'Edit email template' : 'New email template'} destroyOnClose
      extra={<Space><Button onClick={onClose}>Cancel</Button><Button type="primary" loading={saving} onClick={save}>{templateId ? 'Update' : 'Create'}</Button></Space>}>
      <style>{`
        @media (max-width: 980px) {
          .template-editor-workspace {
            grid-template-columns: 1fr !important;
          }
          .template-placeholder-panel {
            position: relative !important;
            max-height: 420px !important;
          }
        }
      `}</style>
      <Form form={form} layout="vertical" initialValues={EMPTY} disabled={loading} className="template-editor-form">
        <div className="template-editor-grid">
          <Form.Item name="name" label="Template name" rules={[{ required: true }]}><Input placeholder="Candidate introduction" /></Form.Item>
          <Form.Item name="module" label="Module" rules={[{ required: true }]}><Select showSearch optionFilterProp="label" options={modules} onChange={(module) => { form.setFieldValue('action', ''); loadModuleActions(module); }} /></Form.Item>
          <Form.Item name="action" label="Action" rules={[{ required: true }]}
            tooltip={'Pick a Row Action, or TYPE a system action key. Not every email comes from a row action — the credentials mail sent when an employer\'s recruiters are provisioned uses "accountCreated", which is raised by a workflow and so never appears in this list.'}>
            <Select showSearch mode="tags" maxCount={1} optionFilterProp="label" options={actionOptions}
              placeholder="Select a row action, or type a system key (e.g. accountCreated)" />
          </Form.Item>
          <Form.Item name="clientId" label="Client scope (optional)"><Select allowClear showSearch optionFilterProp="label" options={clients} placeholder="Available to every client" /></Form.Item>
        </div>
        <Form.Item name="description" label="Description"><Input.TextArea rows={2} /></Form.Item>
        <div className="template-editor-grid template-editor-grid--three">
          <Form.Item name="toMail" label="Default To"><Select mode="tags" open={false} tokenSeparators={[',']} /></Form.Item>
          <Form.Item name="ccMail" label="Default Cc"><Select mode="tags" open={false} tokenSeparators={[',']} /></Form.Item>
          <Form.Item name="bccMail" label="Default Bcc"><Select mode="tags" open={false} tokenSeparators={[',']} /></Form.Item>
        </div>
        <div
          className="template-editor-workspace"
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.2fr) minmax(360px, 0.8fr)',
            gap: 20,
            alignItems: 'start',
            marginTop: 4,
          }}
        >
          <div
            className="template-editor-compose-pane"
            style={{
              minWidth: 0,
              padding: 16,
              border: '1px solid #e5e7eb',
              borderRadius: 10,
              background: '#fff',
            }}
          >
            <Text strong style={{ display: 'block', marginBottom: 12 }}>Email content</Text>
            <Form.Item name="subject" label="Subject" rules={[{ required: true }]}>
              <Input
                placeholder="Email subject — drag placeholders here"
                onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = 'copy'; }}
              />
            </Form.Item>
            <Form.Item label="Message" required style={{ marginBottom: 0 }}>
              <div
                onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = 'copy'; }}
                style={{ minHeight: 320 }}
              >
                <TipTapEditor
                  value={body}
                  onChange={setBody}
                  placeholder="Write the reusable email body… Drag placeholders from the right panel."
                />
              </div>
            </Form.Item>
          </div>

          <div
            className="template-placeholder-panel"
            style={{
              position: 'sticky',
              top: 0,
              maxHeight: 'calc(100vh - 190px)',
              overflowY: 'auto',
              padding: 16,
              border: '1px solid #dbeafe',
              borderRadius: 10,
              background: '#f8fbff',
            }}
          >
            <Text strong style={{ display: 'block' }}>All module placeholders</Text>
            <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
              Drag a placeholder into Subject or Message. You can also click a placeholder to append it to the message.
            </Text>
            {placeholderGroups.map((group) => (
              <div key={group.moduleName} style={{ marginTop: 14 }}>
                <Text type="secondary" strong>{group.label || group.moduleName}</Text>
                <div className="template-placeholder-list" style={{ marginTop: 6 }}>
                  {group.fields.map((field) => (
                    <Tooltip key={`${group.moduleName}:${field.key}:${field.value}`} title={`${field.label || field.key} · ${field.value}`}>
                      <Tag
                        color="blue"
                        draggable
                        onDragStart={(event) => dragPlaceholder(event, field)}
                        onClick={() => insertPlaceholder(field)}
                        style={{ cursor: 'grab', userSelect: 'none', marginBottom: 6 }}
                      >
                        {`{{.${field.key}}}`}
                      </Tag>
                    </Tooltip>
                  ))}
                </div>
              </div>
            ))}
            {!placeholderGroups.length && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No module fields available" />}
          </div>
        </div>

        <Form.Item name="isActive" label="Available in mail composer" valuePropName="checked" style={{ marginTop: 16 }}>
          <Switch />
        </Form.Item>
      </Form>
    </Drawer>
  );
}

function EmailTemplatesPanel() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [modules, setModules] = useState([]);
  const [clients, setClients] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);

  const moduleOptions = useMemo(() => modules.map((item) => ({ value: item.key ?? item.module ?? item.value, label: item.label ?? item.name ?? item.key ?? item.module ?? item.value })).filter((item) => item.value), [modules]);

  function load(nextPage = page, nextSize = pageSize, nextSearch = search) {
    setLoading(true);
    getEmailTemplates({ offset: (nextPage - 1) * nextSize, limit: nextSize, search: nextSearch })
      .then(({ items: data, totalCount }) => { setItems(data); setTotal(totalCount); })
      .catch((error) => message.error(error?.message || 'Could not load templates'))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    queueMicrotask(() => {
      load(1, pageSize, '');
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    Promise.all([getAvailableModules(), getAdminClients({ limit: 200 })]).then(([moduleData, clientData]) => {
      setModules(normalizeModuleOptions(moduleData).map(({ value, label }) => ({ key: value, label })));
      setClients(clientData);
    }).catch(() => {});
  }, []);

  async function toggleStatus(record, checked) {
    const id = idOf(record);
    try { await updateEmailTemplate(id, { ...record, isActive: checked }); setItems((current) => current.map((item) => idOf(item) === id ? { ...item, isActive: checked } : item)); }
    catch (error) { message.error(error?.message || 'Could not update status'); }
  }

  async function remove(id) {
    try { await deleteEmailTemplate(id); message.success('Template deleted'); load(1); setPage(1); }
    catch (error) { message.error(error?.message || 'Could not delete template'); }
  }

  const columns = [
    { title: 'Template', key: 'template', width: 300, ellipsis: true, render: (_, record) => <div className="admin-config-primary-cell templates-primary-cell"><Text strong ellipsis={{ tooltip: record.name }}>{record.name}</Text><Text type="secondary" ellipsis={{ tooltip: record.description || record.subject }}>{record.description || record.subject || 'No description'}</Text></div> },
    { title: 'Module', dataIndex: 'module', key: 'module', width: 140, ellipsis: true, responsive: ['sm'], render: (value) => value ? <Tag color="blue">{value}</Tag> : '—' },
    { title: 'Action', dataIndex: 'action', key: 'action', width: 180, ellipsis: true, responsive: ['md'], render: (value) => value ? <Text code ellipsis={{ tooltip: value }}>{value}</Text> : '—' },
    { title: 'Recipients', key: 'recipients', width: 130, responsive: ['lg'], render: (_, record) => `${(record.toMail?.length ?? 0) + (record.ccMail?.length ?? 0) + (record.bccMail?.length ?? 0)} defaults` },
    { title: 'Active', dataIndex: 'isActive', key: 'active', width: 80, render: (value, record) => <Switch size="small" checked={value} onChange={(checked) => toggleStatus(record, checked)} /> },
    { title: '', key: 'actions', width: 84, fixed: 'right', render: (_, record) => <Space size={2}><Tooltip title="Edit"><Button type="text" icon={<EditOutlined />} onClick={() => { setEditingId(idOf(record)); setEditorOpen(true); }} /></Tooltip><Popconfirm title="Delete this template?" onConfirm={() => remove(idOf(record))}><Button type="text" danger icon={<DeleteOutlined />} /></Popconfirm></Space> },
  ];

  const clientOptions = clients.map((item) => ({ value: item._id ?? item.id, label: item.clientName ?? item.name ?? item._id })).filter((item) => item.value);

  return (
    <div className="fg-section admin-config-section">
      <AppCard icon={<MailOutlined />} title="Email templates" extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingId(null); setEditorOpen(true); }}>New template</Button>}>
        <div className="admin-accent-bar" /><Text type="secondary">Create reusable, module-aware email content. Modules, actions and placeholders are read from DB configuration.</Text>
      </AppCard>
      <AppCard title={`${total} template${total === 1 ? '' : 's'}`} extra={<Button icon={<ReloadOutlined />} onClick={() => load()}>Reload</Button>}>
        <div className="admin-config-table-tools"><Input allowClear prefix={<SearchOutlined />} placeholder="Search templates" value={search} onChange={(event) => { const value = event.target.value; setSearch(value); setPage(1); load(1, pageSize, value); }} /></div>
        <div className="templates-table-viewport">
          <Table rowKey={idOf} columns={columns} dataSource={items} loading={loading} className="fg-table" size="small" tableLayout="fixed" sticky scroll={{ x: 914, y: 'calc(100vh - 410px)' }} pagination={{ current: page, pageSize, total, showSizeChanger: true, responsive: true, onChange: (nextPage, nextSize) => { setPage(nextPage); setPageSize(nextSize); load(nextPage, nextSize); } }} />
        </div>
      </AppCard>
      <TemplateEditor open={editorOpen} templateId={editingId} modules={moduleOptions} clients={clientOptions} onClose={() => setEditorOpen(false)} onSaved={() => { setEditorOpen(false); load(); }} />
    </div>
  );
}

function SignatureEditor({ open, signatureId, onClose, onSaved }) {
  const [name, setName] = useState('');
  const [body, setBody] = useState('');
  const [placeholderMeta, setPlaceholderMeta] = useState({ module: 'userSignature', keys: [] });
  const [saving, setSaving] = useState(false);
  const [loadingPlaceholders, setLoadingPlaceholders] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    queueMicrotask(() => {
      setLoadingPlaceholders(true);
      Promise.all([
        signatureId ? getSignature(signatureId) : Promise.resolve(null),
        getSignaturePlaceholders(),
      ]).then(([item, placeholders]) => {
        if (cancelled) return;
        setName(item?.signatureName ?? '');
        setBody(item?.signature ?? '');
        setPlaceholderMeta(placeholders ?? { module: 'userSignature', keys: [] });
      }).catch(() => {
        if (!cancelled) message.error('Could not load signature details');
      }).finally(() => {
        if (!cancelled) setLoadingPlaceholders(false);
      });
    });
    return () => { cancelled = true; };
  }, [open, signatureId]);

  function insertPlaceholder(item) {
    const token = `{{.${item.key}}}`;
    setBody((current) => `${current}${current ? ' ' : ''}${token}`);
  }

  async function save() {
    if (!name.trim()) return message.error('Signature name is required');
    if (!body.trim()) return message.error('Signature content is required');
    setSaving(true);
    try {
      const used = extractPlaceholderKeys(body);
      const holders = (placeholderMeta.keys ?? [])
        .filter((item) => used.has(item.key))
        .map((item) => ({ key: item.key, value: item.value }));
      const payload = {
        signatureId,
        signatureName: name,
        signature: body,
        signatureLogoPath: '',
        placeHolder: holders.length ? [{ moduleName: placeholderMeta.module || 'userSignature', holders }] : [],
      };
      if (signatureId) await updateSignature(payload); else await createSignature(payload);
      message.success(signatureId ? 'Signature updated' : 'Signature created');
      onSaved();
    } catch (error) { message.error(error?.message || 'Could not save signature'); }
    finally { setSaving(false); }
  }

  const userPlaceholders = (placeholderMeta.keys ?? []).filter((item) => !['Role_id', 'Role_name', 'Reporting_manager'].includes(item.key));
  const rolePlaceholders = (placeholderMeta.keys ?? []).filter((item) => ['Role_id', 'Role_name', 'Reporting_manager'].includes(item.key));
  const renderPlaceholderList = (items) => (
    <div className="template-placeholder-list">
      {items.map((item) => (
        <Tooltip key={item.key} title={`Mongo field: ${item.value}`}>
          <Tag color="blue" style={{ cursor: 'pointer' }} onClick={() => insertPlaceholder(item)}>{`{{.${item.key}}}`}</Tag>
        </Tooltip>
      ))}
    </div>
  );

  return <Drawer open={open} onClose={onClose} width="min(820px, 96vw)" title={signatureId ? 'Edit signature' : 'New signature'} destroyOnClose
    extra={<Space><Button onClick={onClose}>Cancel</Button><Button type="primary" loading={saving} onClick={save}>{signatureId ? 'Update' : 'Create'}</Button></Space>}>
    <Form layout="vertical">
      <Form.Item label="Signature name" required><Input value={name} onChange={(event) => setName(event.target.value)} /></Form.Item>
      <Form.Item label="Signature content" required><TipTapEditor value={body} onChange={setBody} placeholder="Create the reusable signature…" /></Form.Item>
      <div className="template-placeholder-panel">
        <Text strong>Signature placeholders</Text>
        <Text type="secondary">Click a placeholder to add it to the signature. Values resolve from MongoDB users and roles when mail is generated.</Text>
        {loadingPlaceholders ? <Text type="secondary">Loading placeholders…</Text> : <>
          <div style={{ marginTop: 10 }}><Text strong>User details</Text>{renderPlaceholderList(userPlaceholders)}</div>
          <div style={{ marginTop: 10 }}><Text strong>Role details</Text>{renderPlaceholderList(rolePlaceholders)}</div>
          {!placeholderMeta.keys?.length && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No signature placeholders available" />}
        </>}
      </div>
    </Form>
  </Drawer>;
}

function SignatureTemplatesPanel() {
  const [items, setItems] = useState([]); const [total, setTotal] = useState(0); const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(''); const [page, setPage] = useState(1); const [pageSize, setPageSize] = useState(20);
  const [editorOpen, setEditorOpen] = useState(false); const [editingId, setEditingId] = useState(null);
  function load(nextPage = page, nextSize = pageSize, nextSearch = search) {
    setLoading(true); getSignatures({ offset: (nextPage - 1) * nextSize, limit: nextSize, search: nextSearch })
      .then(({ items: data, totalCount }) => { setItems(data); setTotal(totalCount); }).catch(() => message.error('Could not load signatures')).finally(() => setLoading(false));
  }
  useEffect(() => {
    queueMicrotask(() => {
      load(1, pageSize, '');
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  async function toggle(record, checked) { try { await updateSignatureStatus(record.signatureId, checked); setItems((current) => current.map((item) => item.signatureId === record.signatureId ? { ...item, isActive: checked } : item)); } catch { message.error('Could not update status'); } }
  async function remove(id) { try { await deleteSignature(id); message.success('Signature deleted'); setPage(1); load(1); } catch { message.error('Could not delete signature'); } }
  const columns = [
    { title: 'Signature', dataIndex: 'templateName', key: 'name', width: 300, ellipsis: true, render: (value) => <Text strong ellipsis={{ tooltip: value }}>{value || 'Untitled signature'}</Text> },
    { title: 'Created', dataIndex: 'createdDt', key: 'created', width: 150, responsive: ['md'], render: (value) => value ? new Date(value).toLocaleDateString() : '—' },
    { title: 'Active', dataIndex: 'isActive', key: 'active', width: 80, render: (value, record) => <Switch size="small" checked={value} onChange={(checked) => toggle(record, checked)} /> },
    { title: '', key: 'actions', width: 84, fixed: 'right', render: (_, record) => <Space size={2}><Button type="text" icon={<EditOutlined />} onClick={() => { setEditingId(record.signatureId); setEditorOpen(true); }} /><Popconfirm title="Delete this signature?" onConfirm={() => remove(record.signatureId)}><Button type="text" danger icon={<DeleteOutlined />} /></Popconfirm></Space> },
  ];
  return <div className="fg-section admin-config-section"><AppCard icon={<MailOutlined />} title="Signature templates" extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingId(null); setEditorOpen(true); }}>New signature</Button>}><Text type="secondary">Existing company and user signatures from the shared DB.</Text></AppCard><AppCard title={`${total} signature${total === 1 ? '' : 's'}`}><div className="admin-config-table-tools"><Input allowClear prefix={<SearchOutlined />} value={search} placeholder="Search signatures" onChange={(event) => { setSearch(event.target.value); setPage(1); load(1, pageSize, event.target.value); }} /></div><div className="templates-table-viewport"><Table rowKey="signatureId" dataSource={items} columns={columns} loading={loading} className="fg-table" size="small" tableLayout="fixed" sticky scroll={{ x: 614, y: 'calc(100vh - 410px)' }} pagination={{ current: page, pageSize, total, responsive: true, onChange: (p, size) => { setPage(p); setPageSize(size); load(p, size); } }} /></div></AppCard><SignatureEditor open={editorOpen} signatureId={editingId} onClose={() => setEditorOpen(false)} onSaved={() => { setEditorOpen(false); load(); }} /></div>;
}

export default function TemplatesSection() {
  return <Tabs defaultActiveKey="email" className="admin-config-tabs" items={[
    { key: 'email', label: 'Email templates', children: <EmailTemplatesPanel /> },
    { key: 'signature', label: 'Signatures', children: <SignatureTemplatesPanel /> },
  ]} />;
}
